/**
 * ClusterDrawer — cluster detail panel (side or bottom variant).
 */
import { motion } from 'framer-motion';
import { Copy, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Dataset, ClusterId, ClusterDefinition } from '@/data/clusterscope-data';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

interface ClusterDrawerProps {
  dataset: Dataset;
  clusterId: ClusterId;
  selectedNodeId: string | null;
  onClose: () => void;
  variant?: 'side' | 'bottom';
}

function getClusterSummary(cluster: ClusterDefinition | undefined): string {
  if (!cluster) return 'Cluster analysis unavailable.';
  const normalized = cluster.name.toLowerCase().replace(/\s+/g, '');

  if (normalized.includes('smartmoney'))
    return 'These wallets demonstrate coordinated, profitable trading behaviour with consistently high win rates and early entries into strong-performing tokens. The cluster appears to represent experienced traders with disciplined execution.';
  if (normalized.includes('fastflipper'))
    return 'This cluster reacts quickly to new opportunities, entering positions shortly after token launches. Trading frequency is high, although holding periods are relatively short.';
  if (normalized.includes('retailmomentum') || normalized.includes('freshwallet'))
    return 'This cluster consists primarily of recently active wallets with limited historical activity, suggesting newly funded participants or fresh trading entities.';
  if (normalized.includes('whale') || normalized.includes('memewhale') || normalized.includes('dormantwhale'))
    return 'This cluster contains high-capital wallets responsible for a significant share of trading volume. Individual transactions are larger than average and may influence market liquidity.';
  if (normalized.includes('earlyaccumulator'))
    return 'These wallets show a pattern of entering positions well before broader market awareness. Hold times are longer than average, suggesting conviction-based accumulation rather than speculation.';
  if (normalized.includes('coreholder'))
    return 'This cluster exhibits low turnover and extended holding periods. Activity is infrequent but deliberate, consistent with long-term holders rather than active traders.';
  if (normalized.includes('distribution'))
    return 'These wallets appear to systematically distribute holdings across multiple counterparties. Exit timing is synchronized, suggesting coordinated liquidity management.';
  if (normalized.includes('exitliquidity'))
    return 'This cluster is associated with late-stage exits and selling into strength. Position durations are short and exits tend to coincide with peak volume periods.';
  if (normalized.includes('copytrader') || normalized.includes('mirror'))
    return 'These wallets closely follow the trades of a lead entity with minimal delay. Behaviour is reactive rather than predictive, consistent with signal-based or copy-trading activity.';
  if (normalized.includes('kol') || normalized.includes('influencer'))
    return 'This cluster is linked to public figures or voices with large followings. Their wallet activity may precede or accompany social engagement, indicating potential information asymmetry.';
  if (normalized.includes('developer'))
    return 'These wallets are connected to project development teams or early insiders. Activity may precede public announcements, suggesting privileged access to protocol information.';

  return 'This cluster exhibits coordinated behaviour patterns that distinguish it from random market participation. Wallet activity shows meaningful correlation in timing, tokens, and counterparties.';
}

function getClusterTitle(cluster: ClusterDefinition | undefined): string {
  if (!cluster) return 'UNKNOWN CLUSTER';
  const normalized = cluster.name.toLowerCase().replace(/\s+/g, '');

  if (normalized.includes('smartmoney')) return 'SMART MONEY CLUSTER';
  if (normalized.includes('freshwallet')) return 'FRESH WALLETS CLUSTER';
  if (normalized.includes('highwinrate')) return 'HIGH WINRATE CLUSTER';
  if (normalized.includes('whale')) return 'WHALE CLUSTER';
  if (normalized === 'kol') return 'KOL CLUSTER';
  if (normalized === 'developer') return 'DEVELOPER CLUSTER';

  const base = cluster.name.toUpperCase();
  return base.endsWith('CLUSTER') ? base : `${base} CLUSTER`;
}

export default function ClusterDrawer({ dataset, clusterId, selectedNodeId, onClose, variant = 'side' }: ClusterDrawerProps) {
  const cluster = dataset.clusterMap[clusterId];
  const selectedNode = selectedNodeId ? dataset.graphNodeMap[selectedNodeId] : null;

  const copyAddress = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
    toast.success('Wallet address copied');
  };

  const isBottom = variant === 'bottom';

  return (
    <motion.div
      initial={isBottom ? { y: '100%', opacity: 0 } : { x: 280, opacity: 0 }}
      animate={isBottom ? { y: 0, opacity: 1 } : { x: 0, opacity: 1 }}
      exit={isBottom ? { y: '100%', opacity: 0 } : { x: 280, opacity: 0 }}
      transition={{ duration: 0.4, ease: easeOut }}
      className={
        isBottom
          ? 'fixed inset-x-0 bottom-0 z-40 max-h-[78vh] overflow-y-auto rounded-t-2xl border-t border-white/10 bg-[hsl(228,22%,7%)]/95 backdrop-blur-xl'
          : 'absolute right-0 top-0 z-20 h-full w-full max-w-[280px] overflow-y-auto border-l border-white/[0.08] bg-[hsl(228,22%,7%)]/85 backdrop-blur-xl'
      }
    >
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/[0.08] bg-[hsl(228,22%,7%)]/90 px-5 py-4 backdrop-blur-xl">
        <h3 className="text-sm font-extrabold uppercase tracking-wide text-foreground">
          {getClusterTitle(cluster)}
        </h3>
        <button
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-6 px-5 py-5">
        {/* Stat grid */}
        <div className="grid grid-cols-2 gap-2.5">
          <StatCell value={cluster.clusterPnl} label="Total PnL" color="hsl(var(--primary))" />
          <StatCell value={`${cluster.clusterWinRate}%`} label="Win Rate" />
          <StatCell value={cluster.hub.avgHold} label="Avg Hold" />
          <StatCell value={cluster.hub.trades} label="Trades" />
        </div>

        {/* AI Cluster Summary */}
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            Cluster Insight
          </p>
          <p className="text-xs leading-relaxed text-foreground/80">
            {getClusterSummary(cluster)}
          </p>
        </div>

        {/* Cluster Wallets */}
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            Cluster Wallets
          </p>
          <div className="space-y-2">
            {dataset.graphNodes
              .filter((n) => n.clusterId === clusterId)
              .map((node) => (
                <div
                  key={node.id}
                  className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2"
                >
                  <span className="font-mono text-sm text-foreground">{node.label}</span>
                  <button
                    onClick={() => copyAddress(node.label)}
                    className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:border-white/20 hover:text-foreground"
                  >
                    <Copy className="h-3 w-3" />
                    Copy
                  </button>
                </div>
              ))}
          </div>
        </div>

        {/* Frequently traded */}
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            Frequently Traded
          </p>
          <div className="flex flex-wrap gap-1.5">
            {cluster.frequentTokens.map((t) => (
              <span
                key={t}
                className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] font-medium text-foreground/90 transition-colors hover:border-white/20"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Confidence */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Cluster Confidence
            </p>
            <span className="text-xs font-bold text-foreground">{cluster.confidence}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${cluster.confidence}%` }}
              transition={{ duration: 0.9, ease: easeOut }}
              className="h-full rounded-full"
              style={{ backgroundColor: cluster.color }}
            />
          </div>
          <ul className="mt-3 space-y-1.5">
            {cluster.confidenceDetails.map((d) => (
              <li key={d} className="flex items-start gap-2 text-xs text-muted-foreground">
                <span
                  className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: cluster.color }}
                />
                {d}
              </li>
            ))}
          </ul>
        </div>

        {/* AI Analysis */}
        <div>
          <p className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            AI Analysis
          </p>
          <div className="rounded-xl border border-primary/20 bg-primary/[0.05] p-4">
            <div className="mb-3 flex items-center gap-1.5 text-xs font-bold text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              Behavioral Summary
            </div>
            <div className="space-y-2.5 text-xs">
              <SummaryRow label="Likely Role" value={cluster.behavioralSummary.likelyRole} />
              <SummaryRow label="Trading Style" value={cluster.behavioralSummary.tradingStyle} />
              <SummaryRow label="Accuracy" value={cluster.behavioralSummary.accuracy} />
              <SummaryRow label="Shared Behavior" value={cluster.behavioralSummary.sharedBehavior} />
            </div>
          </div>
        </div>

        {/* Selected node quick view in drawer */}
        {selectedNode && (
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Selected Wallet
            </p>
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3.5">
              <div className="mb-2 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cluster.color }} />
                <span className="font-mono text-sm font-semibold text-foreground">{selectedNode.label}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="text-[11px]">
                  <div className="text-muted-foreground">Balance</div>
                  <div className="font-semibold text-foreground">{selectedNode.balance}</div>
                </div>
                <div className="text-[11px]">
                  <div className="text-muted-foreground">Win Rate</div>
                  <div className="font-semibold text-foreground">{selectedNode.winRate}%</div>
                </div>
                <div className="text-[11px]">
                  <div className="text-muted-foreground">Realized PnL</div>
                  <div className="font-semibold text-foreground">{selectedNode.realizedPnl}</div>
                </div>
                <div className="text-[11px]">
                  <div className="text-muted-foreground">Trades</div>
                  <div className="font-semibold text-foreground">{selectedNode.tradeCount}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function StatCell({ value, label, color }: { value: string; label: string; color?: string }) {
  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-3">
      <div className="text-base font-extrabold" style={color ? { color } : undefined}>
        {value}
      </div>
      <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-white/[0.06] pb-2 last:border-0 last:pb-0 sm:flex-row sm:justify-between sm:gap-3">
      <span className="font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-right text-foreground/90 sm:text-right">{value}</span>
    </div>
  );
}
